import type { ReferenceMeta } from '@volley/crucible-ipc-types';
import { z } from 'zod';
import { aiChat, listReferenceDocs, readReferenceDoc } from '../anvil-api';
import { parseAiJson } from '../schemas';

export const MAX_HIDDEN_REFERENCES = 3;

const MAX_REFERENCE_CANDIDATES = 8;

const HiddenReferenceSelectionSchema = z.object({
  filenames: z.array(z.string()).default([]),
});

const STOPWORDS = new Set([
  'about',
  'across',
  'agent',
  'build',
  'change',
  'design',
  'draft',
  'game',
  'idea',
  'make',
  'more',
  'player',
  'players',
  'project',
  'revision',
  'section',
  'sections',
  'sync',
  'that',
  'this',
  'update',
  'with',
]);

export interface HiddenReferenceContext {
  block: string;
  names: string[];
}

function tokens(text: string): Set<string> {
  return new Set(
    text
      .toLowerCase()
      .split(/[^a-z0-9]+/g)
      .filter((token) => token.length >= 4 && !STOPWORDS.has(token)),
  );
}

function referenceMetadataText(ref: ReferenceMeta): string {
  return [ref.title, ref.filename.replace(/\.md$/i, ''), ref.sourceUrl ?? ''].join(' ');
}

function candidateScore(intentTokens: Set<string>, ref: ReferenceMeta): number {
  const refTokens = tokens(referenceMetadataText(ref));
  let score = 0;
  for (const token of intentTokens) {
    if (refTokens.has(token)) score += 1;
  }
  return score;
}

export async function selectHiddenReferenceDocs({
  gameId,
  intent,
  references,
}: {
  gameId: string;
  intent: string;
  references: ReferenceMeta[];
}): Promise<ReferenceMeta[]> {
  const intentTokens = tokens(intent);
  if (intentTokens.size === 0 || references.length === 0) return [];

  const candidates = references
    .map((ref) => ({ ref, score: candidateScore(intentTokens, ref) }))
    .filter(({ score }) => score >= 2)
    .sort((a, b) => b.score - a.score || a.ref.title.localeCompare(b.ref.title))
    .slice(0, MAX_REFERENCE_CANDIDATES);
  if (candidates.length === 0) return [];

  try {
    const response = await aiChat({
      gameId,
      system: [
        'You conservatively choose hidden design reference docs for a single Design-tab agent run.',
        `Return at most ${MAX_HIDDEN_REFERENCES} filenames, but prefer [] unless metadata strongly matches the current intent.`,
        'You only see metadata. Do not guess from filenames with broad or generic overlap.',
        'Output strict JSON: { "filenames": ["reference.md"] }',
      ].join('\n'),
      messages: [
        {
          role: 'user',
          content: JSON.stringify({
            intent,
            references: candidates.map(({ ref }) => ({
              filename: ref.filename,
              title: ref.title,
              sourceUrl: ref.sourceUrl ?? null,
              sizeBytes: ref.sizeBytes,
              fetchedAt: ref.fetchedAt ?? null,
            })),
          }),
        },
      ],
      maxTokens: 1024,
    });
    const parsed = parseAiJson(response.text, HiddenReferenceSelectionSchema);
    const candidateMap = new Map(candidates.map(({ ref }) => [ref.filename, ref]));
    const selected: ReferenceMeta[] = [];
    for (const filename of parsed.filenames ?? []) {
      const ref = candidateMap.get(filename);
      if (!ref || selected.some((existing) => existing.filename === filename)) continue;
      selected.push(ref);
      if (selected.length >= MAX_HIDDEN_REFERENCES) break;
    }
    return selected;
  } catch {
    return [];
  }
}

export async function buildHiddenReferenceContext({
  gameId,
  intent,
}: {
  gameId: string;
  intent: string;
}): Promise<HiddenReferenceContext> {
  try {
    const references = await listReferenceDocs();
    const selected = await selectHiddenReferenceDocs({ gameId, intent, references });
    if (selected.length === 0) return { block: '', names: [] };

    const parts: string[] = [
      '\n\n# Reference material\nThese hidden Weekend design references were selected conservatively from metadata. Use them as background only when directly relevant.\n',
    ];
    const names: string[] = [];
    for (const ref of selected) {
      try {
        const body = await readReferenceDoc(ref.filename);
        if (!body.trim()) continue;
        parts.push(`\n## Reference: ${ref.title}\n${body}\n`);
        names.push(ref.title);
      } catch {
        // Hidden references fail closed; unreadable docs are omitted.
      }
    }
    return names.length > 0 ? { block: parts.join(''), names } : { block: '', names: [] };
  } catch {
    return { block: '', names: [] };
  }
}

export function formatReferenceUsage(names: string[]): string {
  return names.length > 0 ? `References used: ${names.join(', ')}` : 'No reference docs used';
}
